import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, ZoomOut, RotateCw } from "lucide-react";

function getCroppedBlob(imageSrc, crop, rotation, output = 512) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = output; canvas.height = output;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#fff";
            ctx.fillRect(0, 0, output, output);
            const rad = (rotation * Math.PI) / 180;
            ctx.save();
            ctx.beginPath(); ctx.arc(output / 2, output / 2, output / 2, 0, Math.PI * 2); ctx.clip();
            ctx.translate(output / 2, output / 2);
            ctx.rotate(rad);
            ctx.translate(-output / 2, -output / 2);
            ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, 0, 0, output, output);
            ctx.restore();
            canvas.toBlob((b) => b ? resolve(b) : reject(new Error("no blob")), "image/jpeg", 0.88);
        };
        img.onerror = reject;
        img.src = imageSrc;
    });
}

export default function ImageCropper({ open, onOpenChange, imageSrc, aspect = 1, onCropped, title = "قص الصورة" }) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [pixels, setPixels] = useState(null);
    const [busy, setBusy] = useState(false);

    const onComplete = useCallback((_, px) => setPixels(px), []);

    const save = async () => {
        if (!pixels) return;
        setBusy(true);
        try {
            const blob = await getCroppedBlob(imageSrc, pixels, rotation);
            const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });
            await onCropped(file);
            onOpenChange(false);
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent dir="rtl" className="max-w-lg">
                <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
                <div className="relative w-full h-72 bg-slate-900 rounded-lg overflow-hidden">
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            rotation={rotation}
                            aspect={aspect}
                            cropShape={aspect === 1 ? "round" : "rect"}
                            showGrid={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onComplete}
                        />
                    )}
                </div>
                <div className="space-y-3">
                    <div className="flex items-center gap-3">
                        <ZoomOut className="w-4 h-4 text-slate-500" />
                        <Slider value={[zoom]} min={1} max={4} step={0.05} onValueChange={([v]) => setZoom(v)} className="flex-1" data-testid="crop-zoom" />
                        <ZoomIn className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="flex items-center gap-3">
                        <RotateCw className="w-4 h-4 text-slate-500" />
                        <Slider value={[rotation]} min={0} max={360} step={1} onValueChange={([v]) => setRotation(v)} className="flex-1" data-testid="crop-rotate" />
                        <span className="text-xs text-slate-500 font-mono-num w-8">{rotation}°</span>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
                    <Button onClick={save} disabled={busy || !pixels} data-testid="btn-crop-save" className="bg-[#1B2A4E] hover:bg-[#0F172A]">
                        {busy ? "جارٍ الحفظ..." : "حفظ الصورة"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
